import React from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

const LegalNoticePage: React.FC = () => {
  const theme = useTheme();

  const sectionHeaderSx = {
    color: theme.palette.text.primary,
    mt: 5,
    mb: 2,
    fontWeight: 600,
  };

  const bodySx = {
    color: theme.palette.text.secondary,
    mb: 2,
    lineHeight: 1.8,
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.background.default,
        py: 6,
      }}
    >
      <Container maxWidth="md">
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={RouterLink} to="/" color="inherit" underline="hover">
            Home
          </Link>
          <Typography color="text.primary">Legal Notice</Typography>
        </Breadcrumbs>

        <Typography variant="h3" sx={{ color: theme.palette.text.primary, mb: 1, fontWeight: 700 }}>
          Legal Notice
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
          Mentions Légales
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
          Last updated: March 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Company Information */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          1. Company Information
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          In accordance with the Luxembourg Law of 14 August 2000 on electronic commerce (transposing
          Directive 2000/31/EC), the following information is provided:
        </Typography>

        <TableContainer component={Paper} sx={{ mb: 3, bgcolor: theme.palette.background.paper }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: '35%' }}>Company name</TableCell>
                <TableCell>AmudFin S.A.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Legal form</TableCell>
                <TableCell>Société Anonyme (S.A.) under Luxembourg law</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Registered office</TableCell>
                <TableCell>9, Boulevard Prince Henri, L-1724 Luxembourg, Grand Duchy of Luxembourg</TableCell>
              </TableRow>
              <TableRow>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>VAT number</TableCell>
                <TableCell>LU34927816</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Share capital</TableCell>
                <TableCell>EUR 30,000 (fully paid up)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Managing Director</TableCell>
                <TableCell>Board of Directors (Conseil d&apos;Administration)</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell>contact@amudfin.com</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Website</TableCell>
                <TableCell>https://amudfin.com</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />

        {/* Hosting Provider */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          2. Hosting Provider
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The AmudFin platform is hosted within the European Union. All data processing and storage
          occurs exclusively within EU/EEA data centres. The hosting infrastructure is provided by
          a professional cloud services provider operating under EU data protection law, with data
          centres located in the EU. Details of our sub-processors are available in our{' '}
          <Link component={RouterLink} to="/data-processing" color="primary">
            Data Processing Agreement
          </Link>.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Regulatory Status */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          3. Regulatory Status
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin S.A. is a software technology company providing operational infrastructure for fund
          management. AmudFin is <strong>not</strong> a regulated financial institution,
          credit institution, investment fund, alternative investment fund manager (AIFM),
          management company (société de gestion), or investment adviser under Luxembourg or
          European Union financial services law.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          AmudFin does not hold a licence or authorisation from the Commission de Surveillance du
          Secteur Financier (CSSF), the European Central Bank (ECB), or any other financial
          supervisory authority. The Platform does not constitute financial advice, investment
          advice, or any regulated financial service.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          Clients of the Platform who are regulated entities remain solely responsible for their own
          regulatory compliance obligations. The use of AmudFin software does not transfer, reduce,
          or satisfy any regulatory obligation of the Client.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Intellectual Property */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          4. Intellectual Property
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The AmudFin name, logo, and all content published on this website (including text, graphics,
          software, photographs, and other materials) are protected by intellectual property laws of
          the Grand Duchy of Luxembourg and international treaties. All rights are reserved.
          Reproduction, distribution, or use of any content without the prior written consent of
          AmudFin S.A. is prohibited, except as permitted under applicable copyright exceptions.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Liability Disclaimer */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          5. Disclaimer
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          The information provided on this website is for general informational purposes only and
          does not constitute professional advice of any kind. While AmudFin endeavours to keep
          the information accurate and current, we make no representations or warranties of any
          kind, express or implied, about the completeness, accuracy, reliability, or suitability
          of the information contained on this website.
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          External links on this website are provided for convenience only. AmudFin does not endorse
          and is not responsible for the content, accuracy, or practices of any third-party websites.
        </Typography>

        <Divider sx={{ my: 3 }} />

        {/* Contact */}
        <Typography variant="h5" sx={sectionHeaderSx}>
          6. Contact
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          For any questions regarding this legal notice or the AmudFin platform:
        </Typography>
        <Typography variant="body1" sx={bodySx}>
          <strong>AmudFin S.A.</strong><br />
          9, Boulevard Prince Henri<br />
          L-1724 Luxembourg<br />
          Grand Duchy of Luxembourg<br />
          Email: legal@amudfin.com
        </Typography>

        <Box sx={{ mt: 6, mb: 4 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            This legal notice is published in accordance with Luxembourg law and EU e-commerce
            directives.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LegalNoticePage;
